import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Resource } from "../../core/models/wrappers/Resource";
import { useApiClient } from "../clients/ApiClient";
import { PageQuery } from "../../utils/models/pageQuery";
import { PagedResponse } from "../models/responses/pagedResponse";
import { PlaceOrderQueueItem } from "../../core/models/entities/PlaceOrderQueueItem";
import { mapToPlaceOrderQueueItem } from "../models/mappers/mapPlaceOrderQueueItem";
import { adminSwrListOptions, adminSwrOptions } from "./adminSwr";
import { enviroment } from "../../enviroment";

export interface PlaceOrderQueueRepository {
  useQueueItems(
    query: PageQuery,
    options?: { refetchInterval?: number },
  ): Resource<PagedResponse<PlaceOrderQueueItem>>;
  useQueueItem(id: number): Resource<PlaceOrderQueueItem>;
  retry(id: number): Promise<PlaceOrderQueueItem>;
}

const QUEUE_ROOT_KEY = "placeOrderQueue";

export function usePlaceOrderQueueRepository(): PlaceOrderQueueRepository {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  function useQueueItems(
    query: PageQuery,
    options?: { refetchInterval?: number },
  ): Resource<PagedResponse<PlaceOrderQueueItem>> {
    return useQuery(
      [QUEUE_ROOT_KEY, "list", query],
      async (): Promise<PagedResponse<PlaceOrderQueueItem>> => {
        const dto = await apiClient.getPlaceOrderQueueItems(query);
        return {
          ...dto,
          results: dto.results?.map(mapToPlaceOrderQueueItem) ?? [],
        };
      },
      {
        refetchInterval: options?.refetchInterval,
        // The queue surface is monitoring; we want fresh data on every nav back.
        staleTime: 0,
        // Admin perf FE-3 (flag): paint from cache + one background refetch,
        // previous page stays visible while the next one loads.
        ...adminSwrListOptions(),
      },
    );
  }

  function useQueueItem(id: number): Resource<PlaceOrderQueueItem> {
    return useQuery(
      [QUEUE_ROOT_KEY, "item", id],
      async () =>
        mapToPlaceOrderQueueItem(await apiClient.getPlaceOrderQueueItem(id)),
      {
        enabled: id > 0,
        staleTime: 0,
        // Detail by id: no keepPreviousData (never show another item).
        ...adminSwrOptions(),
      },
    );
  }

  async function retry(id: number): Promise<PlaceOrderQueueItem> {
    const result = mapToPlaceOrderQueueItem(
      await apiClient.retryPlaceOrderQueueItem(id),
    );
    await queryClient.invalidateQueries({ queryKey: [QUEUE_ROOT_KEY] });
    // Admin perf FE-5 (flag adminPerfOrdersStaleEnabled): the order list is
    // fresh for 60 s with the flag, so a retried queue item (its order's
    // status moves) must mark the ["orders"] keys stale here - the only
    // order-changing mutation outside OrderRepository. Off = unchanged.
    if (enviroment.adminPerfOrdersStaleEnabled) {
      await queryClient.invalidateQueries({ queryKey: ["orders"] });
    }
    return result;
  }

  return {
    useQueueItems,
    useQueueItem,
    retry,
  };
}
